import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import {
  Shield, Plus, Loader2, Building2, Car, User, FileText, Trash2, Pencil,
  Upload, Eye, EyeOff, Download, AlertTriangle, CalendarClock, Truck, X,
  Globe, Copy, Search, DollarSign, Lock, KeyRound, Star,
} from 'lucide-react';

const CATEGORY_META = {
  empresa: { label: 'Empresa', icon: Building2, color: 'from-indigo-500 to-blue-600' },
  veiculo: { label: 'Veículo', icon: Car, color: 'from-emerald-500 to-teal-600' },
  particular: { label: 'Particular', icon: Car, color: 'from-rose-500 to-pink-600' },
  pessoal: { label: 'Pessoal', icon: User, color: 'from-amber-500 to-orange-600' },
  outro: { label: 'Outro', icon: Shield, color: 'from-slate-500 to-slate-700' },
};
const COMPANY_TYPE_LABEL = { GL: 'General Liability (GL)', WC: "Workers' Comp (WC)", outro: 'Outro' };

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso + 'T00:00:00').toLocaleDateString('en-US', { timeZone: 'America/New_York', month: '2-digit', day: '2-digit', year: 'numeric' }); } catch { return iso; }
};
// Normaliza qualquer data (ISO, MM/AAAA, MM/DD/AAAA) para 'YYYY-MM-DD' aceito pelo <input type="date">
const toDateInput = (v) => {
  if (!v) return '';
  const s = String(v).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  let m = s.match(/^(\d{1,2})\/(\d{4})$/); // MM/AAAA -> último dia do mês
  if (m) { const mm = +m[1], yy = +m[2]; if (mm >= 1 && mm <= 12) { const last = new Date(yy, mm, 0).getDate(); return `${yy}-${String(mm).padStart(2, '0')}-${String(last).padStart(2, '0')}`; } }
  m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/); // MM/DD/AAAA
  if (m) { const a = +m[1], b = +m[2], yy = +m[3]; const d = new Date(yy, a - 1, b); if (!Number.isNaN(d.getTime())) return `${yy}-${String(a).padStart(2, '0')}-${String(b).padStart(2, '0')}`; }
  const d = new Date(s);
  if (!Number.isNaN(d.getTime())) return d.toISOString().slice(0, 10);
  return '';
};
const fmtMoney = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  if (Number.isNaN(n)) return null;
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const validadeBadge = (daysUntil) => {
  if (daysUntil === null || daysUntil === undefined) return { text: 'Sem validade', cls: 'bg-slate-100 text-slate-500' };
  if (daysUntil < 0) return { text: `Vencido há ${Math.abs(daysUntil)}d`, cls: 'bg-red-100 text-red-700' };
  if (daysUntil === 0) return { text: 'Vence hoje', cls: 'bg-red-100 text-red-700' };
  if (daysUntil <= 30) return { text: `Vence em ${daysUntil}d`, cls: 'bg-amber-100 text-amber-700' };
  return { text: `${daysUntil}d restantes`, cls: 'bg-emerald-100 text-emerald-700' };
};

// Imagem autenticada (busca blob com o token)
const AuthImage = ({ src, alt, className }) => {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let obj;
    let cancelled = false;
    (async () => {
      try {
        const token = sessionStorage.getItem('access_token');
        const res = await axios.get(src, { responseType: 'blob', headers: { Authorization: `Bearer ${token}` } });
        obj = URL.createObjectURL(res.data);
        if (!cancelled) setUrl(obj);
      } catch (e) { if (!cancelled) setUrl(null); }
    })();
    return () => { cancelled = true; if (obj) URL.revokeObjectURL(obj); };
  }, [src]);
  if (!url) return null;
  return <img src={url} alt={alt} className={className} />;
};

const emptyForm = {
  category: 'empresa', company_type: 'GL', insurer: '', policy_number: '', insured: '',
  broker_name: '', broker_company: '', broker_phone: '', broker_email: '',
  truck_ids: [], vehicle_labels: [], start_date: '', expiration_date: '', amount: '', notes: '',
  vault_mode: 'none', vault_item_id: '',
  vnew_name: '', vnew_url: '', vnew_login: '', vnew_password: '',
  bill_mode: 'none', company_bill_id: '',
  bnew_name: '', bnew_value: '', bnew_frequency: 'annual', bnew_next_due: '',
};

export default function InsuranceManager({ API }) {
  const [insurances, setInsurances] = useState([]);
  const [fleet, setFleet] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(false);

  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const [detail, setDetail] = useState(null); // seguro selecionado
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null); // {url, filename, isImage, contentType}
  const fileRef = useRef(null);
  const uploadKeyRef = useRef(null);

  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [validityFilter, setValidityFilter] = useState('all');
  const [showPwd, setShowPwd] = useState(false);
  const [carInput, setCarInput] = useState('');
  useEffect(() => { setShowPwd(false); }, [detail?.id]);

  // Promoção de card da Frota (novo seguro OU adicionar a um existente)
  const [promoteTarget, setPromoteTarget] = useState(null); // {truck_id, label}
  const [promoteMode, setPromoteMode] = useState('new');
  const [promoteExistingId, setPromoteExistingId] = useState('');
  const [promoting, setPromoting] = useState(false);
  // Reconciliação Seguro ↔ Conta a Pagar
  const [reconcile, setReconcile] = useState(null);
  const [reconciling, setReconciling] = useState(false);

  // Cofre (vault) — vinculação de credenciais
  const [vaultNames, setVaultNames] = useState([]);        // [{id,name,url,category}] (sem senha)
  const [vaultUnlocked, setVaultUnlocked] = useState(false);
  const [vaultCreds, setVaultCreds] = useState({});         // id -> {username,password,url,...} (após desbloqueio)
  const [unlockOpen, setUnlockOpen] = useState(false);
  const [unlockPwd, setUnlockPwd] = useState('');
  const [unlocking, setUnlocking] = useState(false);

  const copyText = async (text, label) => {
    try { await navigator.clipboard.writeText(text || ''); toast.success(`${label} copiado!`); } catch (e) { /* noop */ }
  };

  const loadVaultNames = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/vault/names`, { headers: { Authorization: `Bearer ${sessionStorage.getItem('access_token')}` } });
      setVaultNames(Array.isArray(data) ? data : []);
    } catch (e) { /* silencioso */ }
  }, [API]);

  const loadFullVaultCreds = async () => {
    const { data } = await axios.get(`${API}/admin/vault/passwords`, { headers: { Authorization: `Bearer ${sessionStorage.getItem('access_token')}` } });
    const map = {};
    (data || []).forEach((v) => { map[v.id] = v; });
    setVaultCreds(map);
  };

  const revealCreds = async () => {
    // Tenta biometria (FaceID/TouchID) no app nativo; senão pede a senha de acesso
    try {
      if (window.Capacitor) {
        const { NativeBiometric } = await import('@capgo/capacitor-native-biometric');
        const avail = await NativeBiometric.isAvailable();
        if (avail.isAvailable) {
          await NativeBiometric.verifyIdentity({ reason: 'Acessar credenciais do Cofre', title: 'Cofre de Senhas', subtitle: 'Use biometria para ver as credenciais' });
          await loadFullVaultCreds();
          setVaultUnlocked(true);
          return;
        }
      }
    } catch (e) { /* biometria cancelada -> cai para senha */ }
    setUnlockOpen(true);
  };

  const doPasswordUnlock = async () => {
    setUnlocking(true);
    try {
      await axios.post(`${API}/admin/vault/verify`, { password: unlockPwd }, { headers: { Authorization: `Bearer ${sessionStorage.getItem('access_token')}` } });
      await loadFullVaultCreds();
      setVaultUnlocked(true);
      setUnlockOpen(false);
      setUnlockPwd('');
    } catch (e) {
      toast.error('Senha incorreta');
    } finally { setUnlocking(false); }
  };

  const authHeaders = () => ({ headers: { Authorization: `Bearer ${sessionStorage.getItem('access_token')}` } });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/insurances`, authHeaders());
      setInsurances(data.insurances || []);
      setFleet(data.fleet_vehicles || []);
    } catch (e) {
      console.error('Erro ao carregar seguros:', e);
      toast.error('Erro ao carregar seguros');
    } finally { setLoading(false); }
  }, [API]);

  const loadTrucks = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/trucks`, authHeaders());
      setTrucks(Array.isArray(data) ? data : []);
    } catch (e) { /* silencioso */ }
  }, [API]);

  const loadBills = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/company-bills`, authHeaders());
      setBills(Array.isArray(data) ? data : []);
    } catch (e) { /* silencioso */ }
  }, [API]);

  useEffect(() => { load(); loadTrucks(); loadVaultNames(); loadBills(); }, [load, loadTrucks, loadVaultNames, loadBills]);

  const openCreate = () => { setForm(emptyForm); setEditingId(null); setCarInput(''); setFormOpen(true); };
  const openEdit = (ins) => {
    setForm({
      category: ins.category || 'outro',
      company_type: ins.company_type || 'GL',
      insurer: ins.insurer || '', policy_number: ins.policy_number || '', insured: ins.insured || '',
      broker_name: ins.broker_name || '', broker_company: ins.broker_company || '', broker_phone: ins.broker_phone || '', broker_email: ins.broker_email || '',
      truck_ids: Array.isArray(ins.truck_ids) ? ins.truck_ids : (ins.truck_id ? [ins.truck_id] : []),
      vehicle_labels: Array.isArray(ins.vehicle_labels) ? ins.vehicle_labels : (ins.vehicle_label ? [ins.vehicle_label] : []),
      start_date: toDateInput(ins.start_date), expiration_date: toDateInput(ins.expiration_date),
      amount: ins.amount ?? '', notes: ins.notes || '',
      vault_mode: ins.vault_item_id ? 'existing' : 'none',
      vault_item_id: ins.vault_item_id || '',
      vnew_name: '', vnew_url: '', vnew_login: '', vnew_password: '',
      bill_mode: ins.company_bill_id ? 'existing' : 'none',
      company_bill_id: ins.company_bill_id || '',
      bnew_name: '', bnew_value: '', bnew_frequency: 'annual', bnew_next_due: '',
    });
    setEditingId(ins.id);
    setCarInput('');
    setFormOpen(true);
  };

  const truckLabel = (tid) => {
    const t = trucks.find((x) => x.id === tid);
    if (!t) return 'Caminhão';
    return `${t.placa || ''}${t.modelo ? ` - ${t.modelo}` : ''}`.trim() || t.placa || 'Caminhão';
  };
  const addTruckToForm = (tid) => { if (tid && !form.truck_ids.includes(tid)) setForm((f) => ({ ...f, truck_ids: [...f.truck_ids, tid] })); };
  const removeTruckFromForm = (tid) => setForm((f) => ({ ...f, truck_ids: f.truck_ids.filter((x) => x !== tid) }));
  const addCarToForm = () => { const v = carInput.trim(); if (v && !form.vehicle_labels.includes(v)) { setForm((f) => ({ ...f, vehicle_labels: [...f.vehicle_labels, v] })); setCarInput(''); } };
  const removeCarFromForm = (v) => setForm((f) => ({ ...f, vehicle_labels: f.vehicle_labels.filter((x) => x !== v) }));

  const save = async () => {
    const hasVehicles = form.truck_ids.length > 0 || form.vehicle_labels.length > 0;
    if (!form.insurer.trim() && !form.insured.trim() && !hasVehicles) {
      toast.error('Informe ao menos a seguradora, o segurado ou um veículo');
      return;
    }
    setSaving(true);
    try {
      // Resolver vinculação ao Cofre
      let vaultId = '';
      if (form.vault_mode === 'existing') {
        vaultId = form.vault_item_id || '';
      } else if (form.vault_mode === 'new') {
        if (!form.vnew_name.trim() || !form.vnew_password.trim()) {
          toast.error('Para criar no Cofre, informe ao menos o nome e a senha');
          setSaving(false);
          return;
        }
        const { data: created } = await axios.post(`${API}/admin/vault/passwords`, {
          type: 'empresa',
          name: form.vnew_name.trim(),
          category: 'Seguros',
          username: form.vnew_login.trim(),
          password: form.vnew_password,
          url: form.vnew_url.trim(),
          notes: 'Criado a partir de Seguros',
        }, authHeaders());
        vaultId = created?.id || '';
        await loadVaultNames();
      }
      // Resolver vinculação a Contas a Pagar
      let billId = '';
      if (form.bill_mode === 'existing') {
        billId = form.company_bill_id || '';
      } else if (form.bill_mode === 'new') {
        const bName = (form.bnew_name || form.insurer || form.insured || 'Seguro').trim();
        const bVal = form.bnew_value === '' ? (form.amount === '' ? 0 : Number(form.amount)) : Number(form.bnew_value);
        const bNext = form.bnew_next_due || form.expiration_date || '';
        const dueDay = bNext ? Number(bNext.slice(8, 10)) || 1 : 1;
        const { data: createdBill } = await axios.post(`${API}/admin/company-bills`, {
          name: bName,
          category: 'Seguros',
          value: bVal,
          frequency: form.bnew_frequency || 'annual',
          due_day: dueDay,
          next_due_date: bNext,
          notes: 'Criado a partir de Seguros',
        }, authHeaders());
        billId = createdBill?.id || '';
        await loadBills();
      }
      const payload = {
        category: form.category, company_type: form.company_type,
        insurer: form.insurer, policy_number: form.policy_number, insured: form.insured,
        broker_name: form.broker_name, broker_company: form.broker_company, broker_phone: form.broker_phone, broker_email: form.broker_email,
        truck_ids: form.truck_ids, vehicle_labels: form.vehicle_labels,
        start_date: form.start_date, expiration_date: form.expiration_date,
        amount: form.amount === '' ? null : Number(form.amount), notes: form.notes,
        vault_item_id: vaultId,
        company_bill_id: billId,
      };
      let savedId = editingId;
      if (editingId) {
        await axios.put(`${API}/admin/insurances/${editingId}`, payload, authHeaders());
        toast.success('Seguro atualizado');
      } else {
        const { data } = await axios.post(`${API}/admin/insurances`, payload, authHeaders());
        savedId = data.insurance?.id;
        toast.success('Seguro cadastrado');
      }
      setFormOpen(false);
      await load();
      await loadBills();
      // Se estava vendo o detalhe, atualiza
      if (detail && savedId === detail.id) {
        const { data } = await axios.get(`${API}/admin/insurances`, authHeaders());
        setDetail((data.insurances || []).find((x) => x.id === savedId) || null);
      }
      // Reconciliação: valor e validade do seguro devem bater com a Conta a Pagar vinculada
      if (billId && savedId) {
        try {
          const { data: freshBills } = await axios.get(`${API}/admin/company-bills`, authHeaders());
          const bill = (freshBills || []).find((b) => b.id === billId);
          if (bill) {
            const insAmt = payload.amount;
            const insDate = payload.expiration_date || '';
            const billVal = (bill.value === null || bill.value === undefined) ? null : Number(bill.value);
            const billDate = bill.next_due_date || '';
            const amountMismatch = Number(insAmt || 0) !== Number(billVal || 0);
            const dateMismatch = (insDate || '') !== (billDate || '');
            if (amountMismatch || dateMismatch) {
              setReconcile({
                insuranceId: savedId,
                insLabel: (form.insurer || form.insured || 'Seguro'),
                bill,
                amountMismatch, dateMismatch,
                insAmt: (insAmt === null || insAmt === undefined) ? '' : insAmt,
                insDate, billVal: (billVal === null ? '' : billVal), billDate,
                amountChoice: 'ins', amountCustom: '',
                dateChoice: 'ins', dateCustom: '',
              });
            }
          }
        } catch (e) { /* silencioso */ }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao salvar seguro');
    } finally { setSaving(false); }
  };

  const applyReconcile = async () => {
    if (!reconcile) return;
    setReconciling(true);
    try {
      const r = reconcile;
      // valor final
      let finalAmount;
      if (!r.amountMismatch) finalAmount = (r.insAmt === '' ? null : Number(r.insAmt));
      else if (r.amountChoice === 'ins') finalAmount = (r.insAmt === '' ? 0 : Number(r.insAmt));
      else if (r.amountChoice === 'bill') finalAmount = (r.billVal === '' ? 0 : Number(r.billVal));
      else finalAmount = (r.amountCustom === '' ? 0 : Number(r.amountCustom));
      // data final
      let finalDate;
      if (!r.dateMismatch) finalDate = r.insDate || '';
      else if (r.dateChoice === 'ins') finalDate = r.insDate || '';
      else if (r.dateChoice === 'bill') finalDate = r.billDate || '';
      else finalDate = r.dateCustom || '';
      // Atualiza o seguro
      await axios.put(`${API}/admin/insurances/${r.insuranceId}`, { amount: finalAmount, expiration_date: finalDate }, authHeaders());
      // Atualiza a conta (envia objeto completo p/ não apagar outros campos)
      const dueDay = finalDate ? (Number(finalDate.slice(8, 10)) || r.bill.due_day || 1) : (r.bill.due_day || 1);
      await axios.put(`${API}/admin/company-bills/${r.bill.id}`, {
        ...r.bill, value: finalAmount, next_due_date: finalDate, due_day: dueDay,
      }, authHeaders());
      toast.success('Valores sincronizados entre Seguro e Conta a Pagar');
      setReconcile(null);
      await load();
      await loadBills();
      if (detail && detail.id === r.insuranceId) {
        const { data } = await axios.get(`${API}/admin/insurances`, authHeaders());
        setDetail((data.insurances || []).find((x) => x.id === r.insuranceId) || null);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao sincronizar');
    } finally { setReconciling(false); }
  };

  const removeInsurance = async (ins) => {
    if (!window.confirm(`Excluir o seguro "${ins.label}"? Isso remove também o alerta de validade.`)) return;
    try {
      await axios.delete(`${API}/admin/insurances/${ins.id}`, authHeaders());
      toast.success('Seguro excluído');
      setDetail(null);
      await load();
    } catch (e) { toast.error('Erro ao excluir'); }
  };

  const openPromote = (v) => {
    setPromoteTarget(v);
    setPromoteMode('new');
    setPromoteExistingId('');
  };

  const confirmPromote = async () => {
    if (!promoteTarget) return;
    setPromoting(true);
    try {
      let insurance;
      if (promoteMode === 'existing') {
        if (!promoteExistingId) { toast.error('Selecione um seguro'); setPromoting(false); return; }
        const { data } = await axios.post(`${API}/admin/insurances/${promoteExistingId}/attach-truck/${promoteTarget.truck_id}`, {}, authHeaders());
        insurance = data.insurance;
        toast.success('Caminhão adicionado ao seguro');
      } else {
        const { data } = await axios.post(`${API}/admin/insurances/from-truck/${promoteTarget.truck_id}`, {}, authHeaders());
        insurance = data.insurance;
        toast.success('Seguro criado');
      }
      setPromoteTarget(null);
      await load();
      setDetail(insurance || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao gerenciar seguro da frota');
    } finally { setPromoting(false); }
  };

  const refreshDetail = async (id) => {
    const { data } = await axios.get(`${API}/admin/insurances`, authHeaders());
    setInsurances(data.insurances || []);
    setFleet(data.fleet_vehicles || []);
    setDetail((data.insurances || []).find((x) => x.id === id) || null);
  };

  const triggerUpload = (key) => { uploadKeyRef.current = key || null; fileRef.current?.click(); };
  const onUploadDoc = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !detail) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const vk = uploadKeyRef.current;
      if (vk && vk !== 'general') fd.append('vehicle_key', vk);
      await axios.post(`${API}/admin/insurances/${detail.id}/documents`, fd, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem('access_token')}`, 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Documento enviado');
      await refreshDetail(detail.id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro no upload');
    } finally {
      setUploading(false);
      uploadKeyRef.current = null;
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const viewDoc = async (doc, download = false) => {
    try {
      const res = await axios.get(`${API}/admin/insurances/${detail.id}/documents/${doc.file_id}`, { responseType: 'blob', ...authHeaders() });
      const url = URL.createObjectURL(res.data);
      if (download) {
        const a = document.createElement('a');
        a.href = url; a.download = doc.filename || 'documento';
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      } else {
        setPreview({ url, filename: doc.filename || 'Documento', isImage: !!doc.is_image, contentType: doc.content_type });
      }
    } catch (e) { toast.error('Erro ao abrir documento'); }
  };

  const closePreview = () => {
    if (preview?.url) { try { URL.revokeObjectURL(preview.url); } catch (e) { /* noop */ } }
    setPreview(null);
  };

  const deleteDoc = async (doc) => {
    if (!window.confirm(`Remover o documento "${doc.filename}"?`)) return;
    try {
      await axios.delete(`${API}/admin/insurances/${detail.id}/documents/${doc.file_id}`, authHeaders());
      toast.success('Documento removido');
      await refreshDetail(detail.id);
    } catch (e) { toast.error('Erro ao remover'); }
  };

  const setCover = async (fileId, vehicleKey) => {
    try {
      await axios.put(`${API}/admin/insurances/${detail.id}/cover`, { file_id: fileId, vehicle_key: vehicleKey || 'general' }, authHeaders());
      toast.success(vehicleKey && vehicleKey !== 'general' ? 'Capa do veículo definida' : 'Capa do seguro definida');
      await refreshDetail(detail.id);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erro ao definir capa'); }
  };

  // Filtros
  const matchInsurance = (i) => {
    if (typeFilter !== 'all' && i.category !== typeFilter) return false;
    if (validityFilter !== 'all') {
      const d = i.days_until;
      if (validityFilter === 'overdue' && !(d !== null && d < 0)) return false;
      if (validityFilter === 'soon' && !(d !== null && d >= 0 && d <= 30)) return false;
      if (validityFilter === 'valid' && !(d !== null && d > 30)) return false;
      if (validityFilter === 'none' && d !== null) return false;
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      const hay = `${i.label || ''} ${i.insurer || ''} ${i.policy_number || ''} ${i.insured || ''} ${i.vehicle_label || ''}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  };
  const matchFleet = (v) => {
    if (typeFilter !== 'all' && typeFilter !== 'veiculo') return false;
    if (validityFilter !== 'all') {
      const d = v.days_until;
      if (validityFilter === 'overdue' && !(d !== null && d < 0)) return false;
      if (validityFilter === 'soon' && !(d !== null && d >= 0 && d <= 30)) return false;
      if (validityFilter === 'valid' && !(d !== null && d > 30)) return false;
      if (validityFilter === 'none' && d !== null) return false;
    }
    if (search.trim() && !(`${v.label || ''}`.toLowerCase().includes(search.trim().toLowerCase()))) return false;
    return true;
  };

  const filtered = insurances.filter(matchInsurance);
  const filteredFleet = fleet.filter(matchFleet);

  // Grupos (sobre a lista filtrada)
  const companies = filtered.filter((i) => i.category === 'empresa');
  const gl = companies.filter((i) => i.company_type === 'GL');
  const wc = companies.filter((i) => i.company_type === 'WC');
  const otherCo = companies.filter((i) => i.company_type !== 'GL' && i.company_type !== 'WC');
  const vehicles = filtered.filter((i) => i.category === 'veiculo');
  const particulares = filtered.filter((i) => i.category === 'particular');
  const personal = filtered.filter((i) => i.category === 'pessoal');
  const others = filtered.filter((i) => i.category === 'outro');

  // Custo total por categoria (sobre TODOS os seguros, independente do filtro)
  const sumBy = (cat) => insurances.filter((i) => i.category === cat).reduce((s, i) => s + (Number(i.amount) || 0), 0);
  const costs = {
    empresa: sumBy('empresa'), veiculo: sumBy('veiculo'), particular: sumBy('particular'), pessoal: sumBy('pessoal'), outro: sumBy('outro'),
  };
  costs.total = costs.empresa + costs.veiculo + costs.particular + costs.pessoal + costs.outro;

  const Card = ({ ins }) => {
    const meta = CATEGORY_META[ins.category] || CATEGORY_META.outro;
    const Icon = meta.icon;
    const badge = validadeBadge(ins.days_until);
    const money = fmtMoney(ins.amount);
    const covered = ins.covered_vehicles || [];
    const hasFleet = (ins.truck_ids || []).length > 0;
    return (
      <button
        data-testid={`insurance-card-${ins.id}`}
        onClick={() => setDetail(ins)}
        className="group text-left rounded-xl border bg-white shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all overflow-hidden flex flex-col"
      >
        <div className={`relative h-28 bg-gradient-to-br ${meta.color} flex items-center justify-center`}>
          {ins.cover_file_id ? (
            <AuthImage src={`${API}/admin/insurances/${ins.id}/documents/${ins.cover_file_id}`} alt={ins.label} className="absolute inset-0 w-full h-full object-cover" />
          ) : (
            <Icon className="w-10 h-10 text-white/80" />
          )}
          <span className="absolute top-2 right-2 text-[10px] font-semibold bg-white/90 text-slate-700 px-2 py-0.5 rounded-full">
            {hasFleet ? 'Frota' : (ins.category === 'empresa' && ins.company_type && ins.company_type !== 'outro' ? ins.company_type : meta.label)}
          </span>
        </div>
        <div className="p-3 flex-1 flex flex-col gap-1">
          <div className="text-sm font-semibold text-slate-800 truncate">{ins.label}</div>
          {ins.policy_number && <div className="text-xs text-slate-500 truncate">Apólice: {ins.policy_number}</div>}
          {(ins.category === 'veiculo' || ins.category === 'particular') && covered.length > 0 ? (
            <div className="text-xs text-slate-500" data-testid={`insurance-card-vehicles-${ins.id}`}>
              <span className="inline-flex items-center gap-1 text-slate-600 font-medium"><Car className="w-3 h-3" /> {covered.length} veículo(s)</span>
              <div className="truncate">{covered.slice(0, 3).join(', ')}{covered.length > 3 ? ` +${covered.length - 3}` : ''}</div>
            </div>
          ) : (
            ins.insured && <div className="text-xs text-slate-500 truncate">Segurado: {ins.insured}</div>
          )}
          <div className="flex items-center justify-between mt-1.5">
            <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${badge.cls}`}>{badge.text}</span>
            {money && <span className="text-xs font-semibold text-slate-700">{money}</span>}
          </div>
          {ins.doc_count > 0 && (
            <div className="text-[11px] text-slate-400 flex items-center gap-1 mt-0.5"><FileText className="w-3 h-3" /> {ins.doc_count} doc(s)</div>
          )}
          {ins.vault_item_id && (
            <div className="text-[11px] text-indigo-400 flex items-center gap-1"><KeyRound className="w-3 h-3" /> Cofre vinculado</div>
          )}
          {ins.company_bill_id && (
            <div className="text-[11px] text-emerald-500 flex items-center gap-1"><DollarSign className="w-3 h-3" /> Conta a Pagar</div>
          )}
        </div>
      </button>
    );
  };

  const FleetCard = ({ v }) => {
    const badge = validadeBadge(v.days_until);
    return (
      <button
        data-testid={`fleet-insurance-${v.truck_id}`}
        onClick={() => openPromote(v)}
        className="group text-left rounded-xl border border-dashed bg-slate-50 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all overflow-hidden flex flex-col"
      >
        <div className="relative h-28 bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
          <Truck className="w-10 h-10 text-white/80" />
          <span className="absolute top-2 right-2 text-[10px] font-semibold bg-white/90 text-emerald-700 px-2 py-0.5 rounded-full">Frota</span>
        </div>
        <div className="p-3 flex-1 flex flex-col gap-1">
          <div className="text-sm font-semibold text-slate-800 truncate">{v.label}</div>
          <div className="text-xs text-slate-500">Seguro do caminhão · clique para gerenciar</div>
          <div className="mt-1.5"><span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${badge.cls}`}>{badge.text}</span></div>
        </div>
      </button>
    );
  };

  const Section = ({ title, icon: Icon, count, children }) => (
    <div className="mb-8">
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-5 h-5 text-indigo-600" />
        <h3 className="text-base font-bold text-slate-800">{title}</h3>
        <span className="text-xs text-slate-400">({count})</span>
      </div>
      {children}
    </div>
  );

  const Grid = ({ children }) => (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">{children}</div>
  );

  const SubGroup = ({ label, items }) => (
    <div className="mb-4">
      <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">{label} <span className="text-slate-400">({items.length})</span></div>
      {items.length === 0 ? (
        <div className="text-xs text-slate-400 italic border border-dashed rounded-lg py-4 text-center">Nenhum seguro {label} cadastrado</div>
      ) : (
        <Grid>{items.map((i) => <Card key={i.id} ins={i} />)}</Grid>
      )}
    </div>
  );

  return (
    <div data-testid="insurance-manager" className="space-y-2">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <div className="flex items-center gap-2">
          <Shield className="w-5 h-5 text-indigo-600" />
          <div>
            <div className="text-lg font-bold text-slate-800">Seguros</div>
            <div className="text-xs text-slate-500">Companhias, veículos e pessoais — com documentos e validades sincronizadas aos alertas</div>
          </div>
        </div>
        <Button onClick={openCreate} size="sm" data-testid="new-insurance-btn" className="text-xs">
          <Plus className="w-4 h-4 mr-1" /> Novo Seguro
        </Button>
      </div>

      {/* Resumo de custos por categoria */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4" data-testid="insurance-cost-summary">
        {[
          ['Empresa', costs.empresa, 'text-indigo-600'],
          ['Carros', costs.veiculo, 'text-emerald-600'],
          ['Pessoal', costs.pessoal, 'text-amber-600'],
          ['Total', costs.total, 'text-slate-800'],
        ].map(([label, val, cls]) => (
          <div key={label} className="rounded-lg border bg-white p-3 shadow-sm">
            <div className="text-[11px] text-slate-500 flex items-center gap-1"><DollarSign className="w-3 h-3" /> {label}</div>
            <div className={`text-lg font-bold ${cls}`}>{fmtMoney(val) || '$0.00'}</div>
          </div>
        ))}
      </div>

      {/* Busca e filtros */}
      <div className="flex flex-wrap items-center gap-2 mb-5">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="w-4 h-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
          <Input data-testid="insurance-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar por seguradora, apólice, segurado…" className="pl-8 h-9 text-sm" />
        </div>
        <select data-testid="insurance-type-filter" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="border rounded-md px-2 py-2 text-xs bg-white h-9">
          <option value="all">Todos os tipos</option>
          <option value="empresa">Empresa</option>
          <option value="veiculo">Veículo</option>
          <option value="particular">Particular</option>
          <option value="pessoal">Pessoal</option>
          <option value="outro">Outro</option>
        </select>
        <select data-testid="insurance-validity-filter" value={validityFilter} onChange={(e) => setValidityFilter(e.target.value)} className="border rounded-md px-2 py-2 text-xs bg-white h-9">
          <option value="all">Todas as validades</option>
          <option value="overdue">Vencidos</option>
          <option value="soon">Vence em 30 dias</option>
          <option value="valid">Vigentes</option>
          <option value="none">Sem validade</option>
        </select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-slate-500"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Carregando…</div>
      ) : (
        <>
          <Section title="Companhias" icon={Building2} count={companies.length}>
            <SubGroup label="GL" items={gl} />
            <SubGroup label="WC" items={wc} />
            {otherCo.length > 0 && <SubGroup label="Outras" items={otherCo} />}
          </Section>

          <Section title="Carros" icon={Car} count={vehicles.length + filteredFleet.length}>
            {(vehicles.length + filteredFleet.length) === 0 ? (
              <div className="text-xs text-slate-400 italic border border-dashed rounded-lg py-4 text-center">Nenhum seguro de veículo cadastrado</div>
            ) : (
              <Grid>
                {vehicles.map((i) => <Card key={i.id} ins={i} />)}
                {filteredFleet.map((v) => <FleetCard key={v.truck_id} v={v} />)}
              </Grid>
            )}
          </Section>

          {particulares.length > 0 && (
            <Section title="Particular" icon={Car} count={particulares.length}>
              <Grid>{particulares.map((i) => <Card key={i.id} ins={i} />)}</Grid>
            </Section>
          )}

          {personal.length > 0 && (
            <Section title="Pessoais" icon={User} count={personal.length}>
              <Grid>{personal.map((i) => <Card key={i.id} ins={i} />)}</Grid>
            </Section>
          )}
          {others.length > 0 && (
            <Section title="Outros" icon={Shield} count={others.length}>
              <Grid>{others.map((i) => <Card key={i.id} ins={i} />)}</Grid>
            </Section>
          )}
        </>
      )}

      {/* ===== Modal Criar/Editar ===== */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="insurance-form-modal">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Editar Seguro' : 'Novo Seguro'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Tipo</Label>
              <select data-testid="insurance-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full border rounded-md px-2 py-2 text-sm bg-white">
                <option value="empresa">Empresa</option>
                <option value="veiculo">Veículo</option>
                <option value="particular">Particular</option>
                <option value="pessoal">Pessoal</option>
                <option value="outro">Outro</option>
              </select>
            </div>

            {form.category === 'empresa' && (
              <div>
                <Label>Tipo de seguro da empresa</Label>
                <select data-testid="insurance-company-type" value={form.company_type} onChange={(e) => setForm({ ...form, company_type: e.target.value })} className="w-full border rounded-md px-2 py-2 text-sm bg-white">
                  <option value="GL">{COMPANY_TYPE_LABEL.GL}</option>
                  <option value="WC">{COMPANY_TYPE_LABEL.WC}</option>
                  <option value="outro">Outro</option>
                </select>
              </div>
            )}

            {(form.category === 'veiculo' || form.category === 'particular') && (() => {
              const usedByOthers = new Set();
              insurances.forEach((i) => { if (i.id !== editingId) (i.truck_ids || []).forEach((t) => usedByOthers.add(t)); });
              const availableTrucks = trucks.filter((t) => !usedByOthers.has(t.id) && !form.truck_ids.includes(t.id));
              const totalVehicles = form.truck_ids.length + form.vehicle_labels.length;
              const isParticular = form.category === 'particular';
              return (
                <div className="space-y-2 rounded-lg bg-slate-50 p-3">
                  <Label className="text-xs">{isParticular ? 'Carros particulares cobertos' : 'Veículos cobertos por este seguro'} <span className="text-slate-400">({totalVehicles})</span></Label>

                  {/* Dropdown para adicionar caminhão da frota (só para categoria Veículo) */}
                  {!isParticular && (
                    <select data-testid="insurance-add-truck" value="" onChange={(e) => { addTruckToForm(e.target.value); e.target.value = ''; }} className="w-full border rounded-md px-2 py-2 text-sm bg-white">
                      <option value="">+ Adicionar caminhão da frota…</option>
                      {availableTrucks.map((t) => (
                        <option key={t.id} value={t.id}>{t.placa}{t.modelo ? ` - ${t.modelo}` : ''}</option>
                      ))}
                    </select>
                  )}

                  {/* Input para adicionar carro avulso */}
                  <div className="flex gap-2">
                    <Input data-testid="insurance-add-car" value={carInput} onChange={(e) => setCarInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCarToForm(); } }} placeholder={isParticular ? 'Meu carro (ex: Toyota Corolla - ABC1234)' : 'Carro avulso (ex: Honda Civic 2021 - ABC1234)'} className="text-sm" />
                    <Button type="button" size="sm" variant="outline" onClick={addCarToForm} data-testid="insurance-add-car-btn"><Plus className="w-4 h-4" /></Button>
                  </div>

                  {/* Lista dos veículos selecionados */}
                  {totalVehicles === 0 ? (
                    <div className="text-xs text-slate-400 italic text-center py-2">Nenhum veículo adicionado</div>
                  ) : (
                    <div className="space-y-1" data-testid="insurance-vehicles-list">
                      {form.truck_ids.map((tid) => (
                        <div key={tid} className="flex items-center justify-between gap-2 bg-white rounded-md px-2 py-1.5 border" data-testid={`form-truck-item-${tid}`}>
                          <span className="text-xs text-slate-700 truncate flex items-center gap-1.5"><Truck className="w-3.5 h-3.5 text-emerald-600" /> {truckLabel(tid)} <span className="text-[9px] font-semibold bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full">Frota</span></span>
                          <button type="button" onClick={() => removeTruckFromForm(tid)} className="p-0.5 text-slate-400 hover:text-red-600 flex-shrink-0" title="Remover"><X className="w-3.5 h-3.5" /></button>
                        </div>
                      ))}
                      {form.vehicle_labels.map((v) => (
                        <div key={v} className="flex items-center justify-between gap-2 bg-white rounded-md px-2 py-1.5 border" data-testid={`form-car-item-${v}`}>
                          <span className="text-xs text-slate-700 truncate flex items-center gap-1.5"><Car className="w-3.5 h-3.5 text-slate-500" /> {v}</span>
                          <button type="button" onClick={() => removeCarFromForm(v)} className="p-0.5 text-slate-400 hover:text-red-600 flex-shrink-0" title="Remover"><X className="w-3.5 h-3.5" /></button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}

            <div>
              <Label>Seguradora</Label>
              <Input data-testid="insurance-insurer" value={form.insurer} onChange={(e) => setForm({ ...form, insurer: e.target.value })} placeholder="Ex: State Farm" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Nº da apólice</Label>
                <Input data-testid="insurance-policy" value={form.policy_number} onChange={(e) => setForm({ ...form, policy_number: e.target.value })} placeholder="Ex: GL-12345" />
              </div>
              <div>
                <Label>Valor / prêmio</Label>
                <Input type="number" step="0.01" data-testid="insurance-amount" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="0.00" />
              </div>
            </div>
            <div>
              <Label>Item / pessoa segurada</Label>
              <Input data-testid="insurance-insured" value={form.insured} onChange={(e) => setForm({ ...form, insured: e.target.value })} placeholder="Ex: LaborBill LLC / João Silva" />
            </div>
            <div className="rounded-lg bg-slate-50 p-3 space-y-2">
              <div className="text-xs font-semibold text-slate-500 flex items-center gap-1"><User className="w-3.5 h-3.5" /> Broker / Agente</div>
              <div className="grid grid-cols-2 gap-2">
                <Input data-testid="broker-name" value={form.broker_name} onChange={(e) => setForm({ ...form, broker_name: e.target.value })} placeholder="Nome do agente" />
                <Input data-testid="broker-company" value={form.broker_company} onChange={(e) => setForm({ ...form, broker_company: e.target.value })} placeholder="Companhia / corretora" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Input data-testid="broker-phone" value={form.broker_phone} onChange={(e) => setForm({ ...form, broker_phone: e.target.value })} placeholder="Telefone" />
                <Input data-testid="broker-email" type="email" value={form.broker_email} onChange={(e) => setForm({ ...form, broker_email: e.target.value })} placeholder="Email" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Data de início</Label>
                <Input type="date" data-testid="insurance-start" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
              </div>
              <div>
                <Label>Data de validade</Label>
                <Input type="date" data-testid="insurance-expiration" value={form.expiration_date} onChange={(e) => setForm({ ...form, expiration_date: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>Observações</Label>
              <textarea data-testid="insurance-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="w-full border rounded-md px-2 py-2 text-sm" />
            </div>
            <div className="rounded-lg bg-slate-50 p-3 space-y-2">
              <div className="text-xs font-semibold text-slate-500 flex items-center gap-1"><Globe className="w-3.5 h-3.5" /> Credenciais (Cofre)</div>
              <select data-testid="insurance-vault-mode" value={form.vault_mode} onChange={(e) => setForm({ ...form, vault_mode: e.target.value })} className="w-full border rounded-md px-2 py-2 text-sm bg-white">
                <option value="none">Sem credenciais</option>
                <option value="existing">Vincular a uma entrada do Cofre</option>
                <option value="new">Criar nova entrada no Cofre</option>
              </select>
              {form.vault_mode === 'existing' && (
                <select data-testid="insurance-vault-select" value={form.vault_item_id} onChange={(e) => setForm({ ...form, vault_item_id: e.target.value })} className="w-full border rounded-md px-2 py-2 text-sm bg-white">
                  <option value="">— Selecione uma entrada —</option>
                  {vaultNames.map((v) => <option key={v.id} value={v.id}>{v.name}{v.category ? ` · ${v.category}` : ''}</option>)}
                </select>
              )}
              {form.vault_mode === 'new' && (
                <div className="space-y-2">
                  <Input data-testid="vault-new-name" value={form.vnew_name} onChange={(e) => setForm({ ...form, vnew_name: e.target.value })} placeholder="Nome da entrada (ex: Portal State Farm)" />
                  <Input data-testid="vault-new-url" value={form.vnew_url} onChange={(e) => setForm({ ...form, vnew_url: e.target.value })} placeholder="Website (URL)" />
                  <div className="grid grid-cols-2 gap-2">
                    <Input data-testid="vault-new-login" value={form.vnew_login} onChange={(e) => setForm({ ...form, vnew_login: e.target.value })} placeholder="Login / usuário" />
                    <Input data-testid="vault-new-password" value={form.vnew_password} onChange={(e) => setForm({ ...form, vnew_password: e.target.value })} placeholder="Senha" />
                  </div>
                  <p className="text-[11px] text-slate-400">Uma entrada será criada no Cofre (categoria "Seguros") e vinculada a este seguro.</p>
                </div>
              )}
            </div>
            <div className="rounded-lg bg-slate-50 p-3 space-y-2">
              <div className="text-xs font-semibold text-slate-500 flex items-center gap-1"><DollarSign className="w-3.5 h-3.5" /> Contas a Pagar</div>
              <select data-testid="insurance-bill-mode" value={form.bill_mode} onChange={(e) => setForm({ ...form, bill_mode: e.target.value })} className="w-full border rounded-md px-2 py-2 text-sm bg-white">
                <option value="none">Não vincular a Contas a Pagar</option>
                <option value="existing">Vincular a uma conta existente</option>
                <option value="new">Criar nova conta a pagar</option>
              </select>
              {form.bill_mode === 'existing' && (
                <select data-testid="insurance-bill-select" value={form.company_bill_id} onChange={(e) => setForm({ ...form, company_bill_id: e.target.value })} className="w-full border rounded-md px-2 py-2 text-sm bg-white">
                  <option value="">— Selecione uma conta —</option>
                  {bills.map((b) => <option key={b.id} value={b.id}>{b.name}{b.value ? ` · $${b.value}` : ''}</option>)}
                </select>
              )}
              {form.bill_mode === 'new' && (
                <div className="space-y-2">
                  <Input data-testid="bill-new-name" value={form.bnew_name} onChange={(e) => setForm({ ...form, bnew_name: e.target.value })} placeholder={`Nome da conta (padrão: ${form.insurer || 'Seguro'})`} />
                  <div className="grid grid-cols-2 gap-2">
                    <Input data-testid="bill-new-value" type="number" step="0.01" value={form.bnew_value} onChange={(e) => setForm({ ...form, bnew_value: e.target.value })} placeholder={`Valor${form.amount ? ` (padrão: ${form.amount})` : ''}`} />
                    <select data-testid="bill-new-frequency" value={form.bnew_frequency} onChange={(e) => setForm({ ...form, bnew_frequency: e.target.value })} className="border rounded-md px-2 py-2 text-sm bg-white">
                      <option value="weekly">Semanal</option>
                      <option value="monthly">Mensal</option>
                      <option value="quarterly">Trimestral</option>
                      <option value="annual">Anual</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-[11px]">Próximo vencimento</Label>
                    <Input data-testid="bill-new-next-due" type="date" value={form.bnew_next_due} onChange={(e) => setForm({ ...form, bnew_next_due: e.target.value })} />
                  </div>
                  <p className="text-[11px] text-slate-400">Uma conta será criada em Contas a Pagar (categoria "🛡️ Seguros") e vinculada a este seguro.</p>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => setFormOpen(false)} size="sm">Cancelar</Button>
              <Button onClick={save} disabled={saving} size="sm" data-testid="save-insurance-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingId ? 'Salvar' : 'Cadastrar')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ===== Modal Detalhe ===== */}
      <Dialog open={!!detail} onOpenChange={(o) => { if (!o) setDetail(null); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto overflow-x-hidden" data-testid="insurance-detail-modal">
          {detail && (
            <div className="min-w-0 space-y-3">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 flex-wrap break-words min-w-0 pr-6">
                  {detail.label}
                  <span className="text-xs font-normal text-slate-400">
                    {CATEGORY_META[detail.category]?.label}{detail.company_type && detail.company_type !== 'outro' ? ` · ${detail.company_type}` : ''}
                  </span>
                  {(detail.truck_ids || []).length > 0 && <span className="text-[10px] font-semibold bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full" data-testid="detail-frota-badge">Frota</span>}
                </DialogTitle>
              </DialogHeader>

              {detail.cover_file_id ? (
                <button
                  onClick={() => { const d = (detail.documents || []).find((x) => x.file_id === detail.cover_file_id); if (d) viewDoc(d, false); }}
                  className="w-full rounded-lg overflow-hidden border bg-slate-100 mb-2 group"
                  data-testid="insurance-detail-image"
                  title="Ver imagem em tamanho real"
                >
                  <AuthImage src={`${API}/admin/insurances/${detail.id}/documents/${detail.cover_file_id}`} alt={detail.label} className="w-full max-h-64 object-contain mx-auto group-hover:opacity-90 transition-opacity" />
                </button>
              ) : (
                <div className={`w-full h-32 rounded-lg mb-2 bg-gradient-to-br ${(CATEGORY_META[detail.category] || CATEGORY_META.outro).color} flex items-center justify-center`} data-testid="insurance-detail-noimage">
                  {React.createElement((CATEGORY_META[detail.category] || CATEGORY_META.outro).icon, { className: 'w-12 h-12 text-white/80' })}
                </div>
              )}

              <div className="space-y-2 text-sm">
                <div className="grid grid-cols-2 gap-y-1.5 gap-x-3">
                  {detail.insurer && <div><span className="text-slate-400 text-xs block">Seguradora</span>{detail.insurer}</div>}
                  {detail.policy_number && <div><span className="text-slate-400 text-xs block">Apólice</span>{detail.policy_number}</div>}
                  {detail.insured && <div><span className="text-slate-400 text-xs block">Segurado</span>{detail.insured}</div>}
                  {fmtMoney(detail.amount) && <div><span className="text-slate-400 text-xs block">Valor</span>{fmtMoney(detail.amount)}</div>}
                  <div><span className="text-slate-400 text-xs block">Início</span>{fmtDate(detail.start_date)}</div>
                  <div><span className="text-slate-400 text-xs block">Validade</span>
                    <span className="inline-flex items-center gap-1">{fmtDate(detail.expiration_date)}
                      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${validadeBadge(detail.days_until).cls}`}>{validadeBadge(detail.days_until).text}</span>
                    </span>
                  </div>
                </div>
                {detail.notes && <div className="pt-1"><span className="text-slate-400 text-xs block">Observações</span>{detail.notes}</div>}
                {(detail.broker_name || detail.broker_company || detail.broker_phone || detail.broker_email) && (
                  <div className="rounded-lg bg-blue-50 border border-blue-100 p-2.5" data-testid="insurance-detail-broker">
                    <div className="text-[11px] text-blue-600 font-semibold flex items-center gap-1 mb-1"><User className="w-3.5 h-3.5" /> Broker / Agente</div>
                    {detail.broker_name && <div className="text-sm text-slate-700">{detail.broker_name}</div>}
                    {detail.broker_company && <div className="text-xs text-slate-500">{detail.broker_company}</div>}
                    <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-1">
                      {detail.broker_phone && <a href={`tel:${detail.broker_phone}`} className="text-xs text-blue-600 hover:underline" data-testid="broker-phone-link">📞 {detail.broker_phone}</a>}
                      {detail.broker_email && <a href={`mailto:${detail.broker_email}`} className="text-xs text-blue-600 hover:underline break-all" data-testid="broker-email-link">✉️ {detail.broker_email}</a>}
                    </div>
                  </div>
                )}
                {detail.company_bill_id && (() => {
                  const b = bills.find((x) => x.id === detail.company_bill_id);
                  return (
                    <div className="rounded-lg bg-emerald-50 border border-emerald-100 p-2.5 flex items-center gap-2" data-testid="insurance-detail-bill">
                      <DollarSign className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                      <div className="min-w-0">
                        <div className="text-[11px] text-emerald-600 font-semibold">Conta a Pagar vinculada</div>
                        <div className="text-sm text-slate-700 truncate">{b ? `${b.name}${b.value ? ` · $${b.value}` : ''}${b.next_due_date ? ` · vence ${fmtDate(b.next_due_date)}` : ''}` : 'Conta vinculada'}</div>
                      </div>
                    </div>
                  );
                })()}
                {(detail.category === 'veiculo' || detail.category === 'particular') && (detail.covered_vehicles || []).length > 0 && (
                  <div className="rounded-lg bg-slate-50 p-2.5" data-testid="insurance-detail-vehicles">
                    <div className="text-xs font-semibold text-slate-500 flex items-center gap-1 mb-1.5"><Car className="w-3.5 h-3.5" /> Veículos cobertos ({detail.covered_vehicles.length})</div>
                    <div className="space-y-1.5">
                      {(detail.truck_details || []).map((t) => (
                        <div key={t.truck_id} className="flex items-center gap-2 text-sm text-slate-700" data-testid={`detail-truck-${t.truck_id}`}>
                          {t.cover_file_id
                            ? <AuthImage src={`${API}/admin/insurances/${detail.id}/documents/${t.cover_file_id}`} alt={t.label} className="w-9 h-9 rounded object-cover flex-shrink-0 border" />
                            : <span className="w-9 h-9 rounded bg-emerald-100 flex items-center justify-center flex-shrink-0"><Truck className="w-4 h-4 text-emerald-600" /></span>}
                          <span className="truncate">{t.label}</span>
                          <span className="text-[9px] font-semibold bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full flex-shrink-0">Frota</span>
                        </div>
                      ))}
                      {(detail.vehicle_labels || []).map((v) => {
                        const cid = (detail.vehicle_covers_map || {})[`car:${v}`];
                        return (
                          <div key={v} className="flex items-center gap-2 text-sm text-slate-700">
                            {cid
                              ? <AuthImage src={`${API}/admin/insurances/${detail.id}/documents/${cid}`} alt={v} className="w-9 h-9 rounded object-cover flex-shrink-0 border" />
                              : <span className="w-9 h-9 rounded bg-slate-200 flex items-center justify-center flex-shrink-0"><Car className="w-4 h-4 text-slate-500" /></span>}
                            <span className="truncate">{v}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                {detail.vault_item_id && (() => {
                  const meta = vaultNames.find((v) => v.id === detail.vault_item_id);
                  const creds = vaultCreds[detail.vault_item_id];
                  const url = (creds?.url) || meta?.url;
                  return (
                    <div className="rounded-lg bg-slate-50 p-2.5 space-y-1.5" data-testid="insurance-portal-block">
                      <div className="text-xs font-semibold text-slate-500 flex items-center gap-1"><KeyRound className="w-3.5 h-3.5" /> Credenciais (Cofre){meta?.name ? ` · ${meta.name}` : ''}</div>
                      {url && (
                        <div className="text-sm truncate">
                          <a href={url.startsWith('http') ? url : `https://${url}`} target="_blank" rel="noreferrer" className="text-indigo-600 hover:underline" data-testid="insurance-website-link">{url}</a>
                        </div>
                      )}
                      {vaultUnlocked && creds ? (
                        <>
                          <div className="flex items-center justify-between gap-2 text-sm">
                            <span className="text-slate-700 truncate"><span className="text-slate-400 text-xs">Login: </span>{creds.username || '—'}</span>
                            <button onClick={() => copyText(creds.username, 'Login')} className="p-1 text-slate-500 hover:text-indigo-600 flex-shrink-0" title="Copiar login" data-testid="copy-login-btn"><Copy className="w-3.5 h-3.5" /></button>
                          </div>
                          <div className="flex items-center justify-between gap-2 text-sm">
                            <span className="text-slate-700 truncate"><span className="text-slate-400 text-xs">Senha: </span>{showPwd ? (creds.password || '—') : '••••••••'}</span>
                            <div className="flex items-center gap-1 flex-shrink-0">
                              <button onClick={() => setShowPwd(!showPwd)} className="p-1 text-slate-500 hover:text-indigo-600" title={showPwd ? 'Ocultar' : 'Mostrar'} data-testid="toggle-pwd-btn">{showPwd ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}</button>
                              <button onClick={() => copyText(creds.password, 'Senha')} className="p-1 text-slate-500 hover:text-indigo-600" title="Copiar senha" data-testid="copy-pwd-btn"><Copy className="w-3.5 h-3.5" /></button>
                            </div>
                          </div>
                        </>
                      ) : (
                        <Button size="sm" variant="outline" className="w-full text-xs mt-1" onClick={revealCreds} data-testid="unlock-vault-btn">
                          <Lock className="w-3.5 h-3.5 mr-1" /> Desbloquear login e senha (senha de acesso / Face ID)
                        </Button>
                      )}
                    </div>
                  );
                })()}
                {detail.days_until !== null && detail.days_until <= 30 && (
                  <div className="flex items-center gap-1.5 text-xs text-amber-700 bg-amber-50 rounded-md px-2 py-1.5">
                    <CalendarClock className="w-3.5 h-3.5" /> Alerta de validade ativo (30 dias antes, anual) — visível no Calendário de Alertas
                  </div>
                )}
              </div>

              {/* Documentos */}
              <div className="mt-3">
                <span className="text-sm font-semibold text-slate-700 flex items-center gap-1.5 mb-2"><FileText className="w-4 h-4" /> Documentos</span>
                <input ref={fileRef} type="file" accept="application/pdf,image/*" className="hidden" onChange={onUploadDoc} data-testid="insurance-doc-input" />
                {(() => {
                  const allDocs = detail.documents || [];
                  const coversMap = detail.vehicle_covers_map || {};
                  const docRow = (doc, groupKey = 'general', currentCoverId = null) => {
                    const isCover = doc.file_id === currentCoverId;
                    return (
                      <div key={doc.file_id} className="flex items-center justify-between gap-2 bg-slate-50 rounded-md px-2 py-1.5">
                        <div className="flex items-center gap-2 min-w-0">
                          {doc.is_image
                            ? <AuthImage src={`${API}/admin/insurances/${detail.id}/documents/${doc.file_id}`} alt={doc.filename} className="w-8 h-8 rounded object-cover flex-shrink-0" />
                            : <FileText className="w-5 h-5 text-red-500 flex-shrink-0" />}
                          <span className="text-xs text-slate-700 truncate">{doc.filename}</span>
                          {isCover && <span className="text-[9px] font-semibold bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full flex-shrink-0">capa</span>}
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          {doc.is_image && (
                            <button title={isCover ? 'Capa atual' : 'Definir como capa'} onClick={() => setCover(doc.file_id, groupKey)} className={`p-1 ${isCover ? 'text-amber-500' : 'text-slate-400 hover:text-amber-500'}`} data-testid={`set-cover-btn-${doc.file_id}`}>
                              <Star className="w-4 h-4" fill={isCover ? 'currentColor' : 'none'} />
                            </button>
                          )}
                          <button title="Ver" onClick={() => viewDoc(doc, false)} className="p-1 text-slate-500 hover:text-indigo-600"><Eye className="w-4 h-4" /></button>
                          <button title="Baixar" onClick={() => viewDoc(doc, true)} className="p-1 text-slate-500 hover:text-indigo-600"><Download className="w-4 h-4" /></button>
                          <button title="Remover" onClick={() => deleteDoc(doc)} className="p-1 text-slate-500 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                        </div>
                      </div>
                    );
                  };
                  const groupBlock = (key, label, iconEl, testidSuffix) => {
                    const docs = allDocs.filter((d) => (d.vehicle_key || 'general') === key);
                    const currentCoverId = key === 'general' ? detail.cover_file_id : coversMap[key];
                    return (
                      <div key={key} className="rounded-lg border p-2 mb-2" data-testid={`insurance-doc-group-${testidSuffix}`}>
                        <div className="flex items-center justify-between mb-1.5 gap-2">
                          <span className="text-xs font-semibold text-slate-600 flex items-center gap-1.5 truncate min-w-0">{iconEl} <span className="truncate">{label}</span> <span className="text-slate-400 flex-shrink-0">({docs.length})</span></span>
                          <Button size="sm" variant="ghost" className="text-[11px] h-7 px-2 flex-shrink-0" onClick={() => triggerUpload(key)} disabled={uploading} data-testid={`upload-doc-btn-${testidSuffix}`}>
                            {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Upload className="w-3.5 h-3.5 mr-1" /> Enviar</>}
                          </Button>
                        </div>
                        {docs.length === 0 ? (
                          <div className="text-[11px] text-slate-400 italic text-center py-1.5">Nenhum documento</div>
                        ) : (
                          <div className="space-y-1.5">{docs.map((d) => docRow(d, key, currentCoverId))}</div>
                        )}
                      </div>
                    );
                  };
                  const isVehicle = (detail.category === 'veiculo' || detail.category === 'particular') && ((detail.truck_details || []).length + (detail.vehicle_labels || []).length) > 0;
                  if (!isVehicle) {
                    return (
                      <>
                        <div className="flex justify-end mb-2">
                          <Button size="sm" variant="outline" className="text-xs" onClick={() => triggerUpload('general')} disabled={uploading} data-testid="upload-doc-btn">
                            {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Upload className="w-3.5 h-3.5 mr-1" /> Enviar</>}
                          </Button>
                        </div>
                        {allDocs.length === 0 ? (
                          <div className="text-xs text-slate-400 italic text-center py-3 border border-dashed rounded-lg">Nenhum documento anexado</div>
                        ) : (
                          <div className="space-y-1.5" data-testid="insurance-docs-list">{allDocs.map((d) => docRow(d, 'general', detail.cover_file_id))}</div>
                        )}
                      </>
                    );
                  }
                  return (
                    <div data-testid="insurance-docs-grouped">
                      <p className="text-[11px] text-slate-400 mb-2">Anexe a apólice/foto de cada veículo, ou use "Geral" para documentos que valem para todos.</p>
                      {groupBlock('general', 'Geral (todos)', <FileText className="w-3.5 h-3.5 text-slate-500" />, 'general')}
                      {(detail.truck_details || []).map((t) => groupBlock(`truck:${t.truck_id}`, t.label, <Truck className="w-3.5 h-3.5 text-emerald-600" />, `truck-${t.truck_id}`))}
                      {(detail.vehicle_labels || []).map((v) => groupBlock(`car:${v}`, v, <Car className="w-3.5 h-3.5 text-slate-500" />, `car-${v}`))}
                    </div>
                  );
                })()}
              </div>

              <div className="flex justify-between items-center pt-4 mt-2 border-t">
                <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50 text-xs" onClick={() => removeInsurance(detail)} data-testid="delete-insurance-btn">
                  <Trash2 className="w-4 h-4 mr-1" /> Excluir
                </Button>
                <Button size="sm" className="text-xs" onClick={() => { const d = detail; setDetail(null); openEdit(d); }} data-testid="edit-insurance-btn">
                  <Pencil className="w-4 h-4 mr-1" /> Editar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal de desbloqueio do Cofre (senha de acesso) */}
      <Dialog open={unlockOpen} onOpenChange={(o) => { if (!o) { setUnlockOpen(false); setUnlockPwd(''); } }}>
        <DialogContent className="max-w-sm" data-testid="vault-unlock-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Lock className="w-4 h-4" /> Desbloquear Cofre</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-500">Digite sua senha de acesso para ver o login e a senha do seguro.</p>
            <Input type="password" data-testid="vault-unlock-input" value={unlockPwd} onChange={(e) => setUnlockPwd(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && doPasswordUnlock()} placeholder="Senha de acesso" autoFocus />
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => { setUnlockOpen(false); setUnlockPwd(''); }}>Cancelar</Button>
              <Button size="sm" onClick={doPasswordUnlock} disabled={unlocking || !unlockPwd} data-testid="vault-unlock-submit">{unlocking ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Desbloquear'}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de promoção de card da Frota (criar novo OU adicionar a existente) */}
      <Dialog open={!!promoteTarget} onOpenChange={(o) => { if (!o) setPromoteTarget(null); }}>
        <DialogContent className="max-w-md" data-testid="promote-fleet-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Truck className="w-4 h-4 text-emerald-600" /> Gerenciar seguro do caminhão</DialogTitle>
          </DialogHeader>
          {promoteTarget && (() => {
            const vehicleInsurances = insurances.filter((i) => i.category === 'veiculo');
            return (
              <div className="space-y-3">
                <div className="text-sm text-slate-600">Caminhão: <span className="font-semibold text-slate-800">{promoteTarget.label}</span></div>
                <div className="space-y-2">
                  <label className="flex items-start gap-2 rounded-lg border p-2.5 cursor-pointer hover:bg-slate-50" data-testid="promote-mode-new">
                    <input type="radio" name="promote-mode" checked={promoteMode === 'new'} onChange={() => setPromoteMode('new')} className="mt-1" />
                    <div><div className="text-sm font-medium text-slate-800">Criar um novo seguro</div><div className="text-xs text-slate-500">Cria um seguro só para este caminhão (você pode adicionar mais depois)</div></div>
                  </label>
                  <label className={`flex items-start gap-2 rounded-lg border p-2.5 ${vehicleInsurances.length === 0 ? 'opacity-50' : 'cursor-pointer hover:bg-slate-50'}`} data-testid="promote-mode-existing">
                    <input type="radio" name="promote-mode" checked={promoteMode === 'existing'} disabled={vehicleInsurances.length === 0} onChange={() => setPromoteMode('existing')} className="mt-1" />
                    <div className="flex-1">
                      <div className="text-sm font-medium text-slate-800">Adicionar a um seguro existente</div>
                      <div className="text-xs text-slate-500 mb-1.5">{vehicleInsurances.length === 0 ? 'Nenhum seguro de veículo cadastrado ainda' : 'Agrupa este caminhão em um seguro que já existe'}</div>
                      {promoteMode === 'existing' && vehicleInsurances.length > 0 && (
                        <select data-testid="promote-existing-select" value={promoteExistingId} onChange={(e) => setPromoteExistingId(e.target.value)} className="w-full border rounded-md px-2 py-1.5 text-sm bg-white">
                          <option value="">— Selecione o seguro —</option>
                          {vehicleInsurances.map((i) => <option key={i.id} value={i.id}>{i.label} ({i.vehicle_count || 0} veículo(s))</option>)}
                        </select>
                      )}
                    </div>
                  </label>
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" size="sm" onClick={() => setPromoteTarget(null)}>Cancelar</Button>
                  <Button size="sm" onClick={confirmPromote} disabled={promoting} data-testid="promote-confirm-btn">
                    {promoting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirmar'}
                  </Button>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Modal de visualização de documento (in-app, sem abrir nova aba) */}
      <Dialog open={!!preview} onOpenChange={(o) => { if (!o) closePreview(); }}>
        <DialogContent className="max-w-4xl w-[95vw] max-h-[92vh] overflow-hidden flex flex-col" data-testid="document-preview-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base truncate pr-8">
              <FileText className="w-4 h-4 text-slate-500 flex-shrink-0" />
              <span className="truncate">{preview?.filename}</span>
            </DialogTitle>
          </DialogHeader>
          {preview && (
            <>
              <div className="flex-1 min-h-0 bg-slate-100 rounded-lg overflow-auto flex items-center justify-center">
                {preview.isImage ? (
                  <img src={preview.url} alt={preview.filename} className="max-w-full max-h-[72vh] object-contain" data-testid="document-preview-image" />
                ) : (
                  <iframe src={preview.url} title={preview.filename} className="w-full h-[72vh] bg-white" data-testid="document-preview-frame" />
                )}
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <a href={preview.url} target="_blank" rel="noreferrer" className="inline-flex"><Button variant="outline" size="sm" data-testid="preview-open-tab-btn"><Globe className="w-3.5 h-3.5 mr-1" /> Abrir em nova aba</Button></a>
                <a href={preview.url} download={preview.filename} className="inline-flex"><Button variant="outline" size="sm" data-testid="preview-download-btn"><Download className="w-3.5 h-3.5 mr-1" /> Baixar</Button></a>
                <Button size="sm" onClick={closePreview} data-testid="preview-close-btn">Fechar</Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal de reconciliação: valor/validade do Seguro devem bater com a Conta a Pagar */}
      <Dialog open={!!reconcile} onOpenChange={(o) => { if (!o) setReconcile(null); }}>
        <DialogContent className="max-w-md overflow-x-hidden" data-testid="reconcile-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base"><AlertTriangle className="w-4 h-4 text-amber-500" /> Divergências com a Conta a Pagar</DialogTitle>
          </DialogHeader>
          {reconcile && (
            <div className="space-y-3 min-w-0">
              <p className="text-sm text-slate-500">O seguro <b>{reconcile.insLabel}</b> e a conta <b>{reconcile.bill?.name}</b> têm valores diferentes. Escolha o correto (ou digite um novo) — os dois serão igualados.</p>

              {reconcile.amountMismatch && (
                <div className="rounded-lg border p-2.5 space-y-2" data-testid="reconcile-amount">
                  <div className="text-xs font-semibold text-slate-600 flex items-center gap-1"><DollarSign className="w-3.5 h-3.5" /> Valor</div>
                  <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="reconcile-amount-ins">
                    <input type="radio" name="rec-amount" checked={reconcile.amountChoice === 'ins'} onChange={() => setReconcile({ ...reconcile, amountChoice: 'ins' })} />
                    <span>Seguro: <b>{fmtMoney(reconcile.insAmt) || '$0.00'}</b></span>
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="reconcile-amount-bill">
                    <input type="radio" name="rec-amount" checked={reconcile.amountChoice === 'bill'} onChange={() => setReconcile({ ...reconcile, amountChoice: 'bill' })} />
                    <span>Conta: <b>{fmtMoney(reconcile.billVal) || '$0.00'}</b></span>
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="radio" name="rec-amount" checked={reconcile.amountChoice === 'custom'} onChange={() => setReconcile({ ...reconcile, amountChoice: 'custom' })} />
                    <span className="flex items-center gap-1">Outro: <Input type="number" step="0.01" className="h-7 w-28 text-sm" value={reconcile.amountCustom} onFocus={() => setReconcile({ ...reconcile, amountChoice: 'custom' })} onChange={(e) => setReconcile({ ...reconcile, amountCustom: e.target.value, amountChoice: 'custom' })} data-testid="reconcile-amount-custom" /></span>
                  </label>
                </div>
              )}

              {reconcile.dateMismatch && (
                <div className="rounded-lg border p-2.5 space-y-2" data-testid="reconcile-date">
                  <div className="text-xs font-semibold text-slate-600 flex items-center gap-1"><CalendarClock className="w-3.5 h-3.5" /> Validade / Vencimento</div>
                  <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="reconcile-date-ins">
                    <input type="radio" name="rec-date" checked={reconcile.dateChoice === 'ins'} onChange={() => setReconcile({ ...reconcile, dateChoice: 'ins' })} />
                    <span>Seguro: <b>{fmtDate(reconcile.insDate)}</b></span>
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="reconcile-date-bill">
                    <input type="radio" name="rec-date" checked={reconcile.dateChoice === 'bill'} onChange={() => setReconcile({ ...reconcile, dateChoice: 'bill' })} />
                    <span>Conta: <b>{fmtDate(reconcile.billDate)}</b></span>
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="radio" name="rec-date" checked={reconcile.dateChoice === 'custom'} onChange={() => setReconcile({ ...reconcile, dateChoice: 'custom' })} />
                    <span className="flex items-center gap-1">Outra: <Input type="date" className="h-7 w-40 text-sm" value={reconcile.dateCustom} onFocus={() => setReconcile({ ...reconcile, dateChoice: 'custom' })} onChange={(e) => setReconcile({ ...reconcile, dateCustom: e.target.value, dateChoice: 'custom' })} data-testid="reconcile-date-custom" /></span>
                  </label>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-1">
                <Button variant="outline" size="sm" onClick={() => setReconcile(null)} data-testid="reconcile-cancel-btn">Manter diferentes</Button>
                <Button size="sm" onClick={applyReconcile} disabled={reconciling} data-testid="reconcile-apply-btn">
                  {reconciling ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Igualar os dois'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
